import { IMAGE_MAX_BYTES, STORED_IMAGE_TYPES, imageTypeOf, type NewImage } from '@realmery/realm-core';

/**
 * Getting a picture file ready to attach (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.2):
 * told by its bytes, not its name; decoded by the browser before it's stored; WebP and
 * BMP turned into PNG, so the desktop (which reads PNG, JPEG, GIF and BMP) shows every
 * picture added here and BMP's uncompressed size isn't kept. At most 10 MB afterwards.
 */

export class ImageFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageFileError';
  }
}

export const NOT_A_PICTURE = "That isn't a picture Realmery can use (PNG, JPEG, GIF, WebP or BMP).";

export interface PreparedImage extends NewImage {
  /** The type the file was, when it was converted to PNG. */
  readonly convertedFrom: 'image/webp' | 'image/bmp' | null;
}

/** What the browser does, kept apart so tests can stand in for it. */
export interface ImageTools {
  /** Decodes the picture, or throws if it can't be. */
  decode(blob: Blob): Promise<ImageBitmap>;
  /** Draws the decoded picture into a PNG. */
  toPng(bitmap: ImageBitmap): Promise<Uint8Array>;
}

export const browserTools: ImageTools = {
  decode: (blob) => createImageBitmap(blob),
  async toPng(bitmap) {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new ImageFileError('The picture could not be converted to PNG.');
    return new Uint8Array(await blob.arrayBuffer());
  },
};

const megabytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export async function prepareImage(file: Pick<File, 'name' | 'arrayBuffer'>, tools: ImageTools = browserTools): Promise<PreparedImage> {
  const data = new Uint8Array(await file.arrayBuffer());
  const type = imageTypeOf(data);
  if (!type) throw new ImageFileError(NOT_A_PICTURE);

  let bitmap: ImageBitmap;
  try {
    bitmap = await tools.decode(new Blob([data as Uint8Array<ArrayBuffer>], { type }));
  } catch {
    throw new ImageFileError('That picture is damaged, or this browser can’t read it.');
  }
  try {
    if (STORED_IMAGE_TYPES.has(type)) {
      if (data.byteLength > IMAGE_MAX_BYTES) throw new ImageFileError(`A picture can be at most 10 MB. This one is ${megabytes(data.byteLength)}.`);
      return { fileName: file.name, data, convertedFrom: null };
    }
    const png = await tools.toPng(bitmap);
    if (png.byteLength > IMAGE_MAX_BYTES) {
      throw new ImageFileError(`A picture can be at most 10 MB. As a PNG, this one is ${megabytes(png.byteLength)}.`);
    }
    return { fileName: file.name.replace(/\.[^.\\/]*$/, '') + '.png', data: png, convertedFrom: type as 'image/webp' | 'image/bmp' };
  } finally {
    bitmap.close();
  }
}
